import { NextImage, Text } from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import React, { useLayoutEffect, useRef, SyntheticEvent } from "react";
import * as Flex from "@repo/ui/flex";
import {
  TYPOGRAPHY_LABEL1,
  TYPOGRAPHY_DISPLAY1,
  TYPOGRAPHY_DISPLAY2,
} from "@/typography-constants";
import AnimatedCtaLink from "../../animated-cta-link/animated-cta-link";
import useClient from "../../hooks/useClient";
import { isMobile, appendRef } from "../../utils";
import { VideoControls } from "../../video-controls";
import { Feature as FeatureType } from "../types";
import styles from "./feature.module.css";

type FeatureProps = {
  feature: FeatureType;
  usedInCarousel?: boolean;
  playVideo?: boolean;
  handleVideoPlaySwitch?: (e: SyntheticEvent<HTMLVideoElement, Event>) => void;
};

const Feature = ({
  handleVideoPlaySwitch,
  usedInCarousel = false,
  playVideo = false,
  feature: {
    id,
    title,
    ctaLink,
    ctaText,
    desktopVideo,
    mobileVideo,
    desktopImage,
    mobileImage,
    textColour,
    displayGradient,
    ctaRef: { jsonValue: ctaRef },
  },
}: FeatureProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isClient = useClient();

  const mobileView = isMobile();
  const isUsedInGrid = !usedInCarousel;
  const isVideo = desktopVideo.jsonValue.value;
  const gradientClass = mobileView
    ? styles.containerGradientTall
    : styles.containerGradientShort;

  const textContainerHeight = usedInCarousel && mobileView ? 104 : 40;
  const textColorValue = textColour.value || "inherit";
  const link = ctaLink.jsonValue;

  const href = link.value.href ?? "";

  const hrefWithCtaRef = appendRef(href, ctaRef.value);

  const videoSrc = mobileView
    ? mobileVideo.jsonValue.value
    : desktopVideo.jsonValue.value;

  useLayoutEffect(() => {
    if (!isClient) return;

    const video = videoRef.current;
    if (!video) {
      return;
    }

    const handlePlayback = async () => {
      try {
        if (playVideo) {
          await video.play();
        } else {
          video.pause();
        }
      } catch (error) {
        console.error("Video playback error:", error);
      }
    };

    handlePlayback();
  }, [playVideo, isClient, mobileView]);

  if (!isClient) {
    return null;
  }

  return (
    <a
      href={usedInCarousel ? hrefWithCtaRef : href}
      className={clsx(
        styles.container,
        displayGradient?.value && gradientClass,
        usedInCarousel && styles[!mobileView ? "hoverOverlay" : ""],
      )}
    >
      {usedInCarousel && !mobileView && (
        <AnimatedCtaLink
          className={clsx(TYPOGRAPHY_LABEL1, styles.hoverCtaLink)}
          textColor={textColorValue}
          link={{
            ...link,
            value: {
              ...link.value,
              href: hrefWithCtaRef,
              text: ctaText.jsonValue.value,
            },
          }}
        />
      )}
      {isVideo && usedInCarousel ? (
        <>
          <video
            id={`video-${id}`}
            playsInline
            loop
            muted
            ref={videoRef}
            preload="metadata"
            role="presentation"
            src={videoSrc}
            onPlay={(e) => {
              if (handleVideoPlaySwitch) handleVideoPlaySwitch(e);
            }}
          />
          <VideoControls
            videoRef={videoRef}
            color="primary"
            size="sm"
            componentName="feature"
          />
        </>
      ) : (
        <NextImage
          field={mobileView ? mobileImage.jsonValue : desktopImage.jsonValue}
          unoptimized
        />
      )}
      <Flex.Container
        column
        gap="3xl"
        justify="end"
        align="center"
        style={{ height: `calc(100% - ${textContainerHeight}px)` }}
        className={styles.titleContainer}
      >
        <Text
          tag="h1"
          style={{ color: textColorValue }}
          className={clsx(
            styles.title,
            isUsedInGrid && mobileView
              ? TYPOGRAPHY_DISPLAY2
              : TYPOGRAPHY_DISPLAY1,
          )}
          field={title}
        />

        {usedInCarousel && mobileView && (
          <AnimatedCtaLink
            className={clsx(TYPOGRAPHY_LABEL1, styles.ctaLink)}
            textColor={textColorValue}
            link={{
              ...link,
              value: {
                ...link.value,
                href: hrefWithCtaRef,
                text: ctaText.jsonValue.value,
              },
            }}
          />
        )}
      </Flex.Container>
    </a>
  );
};

export default Feature;
